import { useState, useCallback } from 'react';
import useMathFormulas from './useMathFormulas';

/**
 * Custom hook to process table data by fetching and integrating math formulas.
 * @param {function} specificFormatTableData - A function that takes (recruits, formulasMapping)
 *                                           and returns the final formatted data array for the table.
 * @returns {object} An object containing:
 *  - processDataWithFormulas: A memoized function to pass to BaseTable's formatData prop.
 *  - isLoadingFormulas: Boolean indicating if a formula fetch is in progress (from the last call).
 *  - formulasError: Error object if the last formula fetch failed.
 */
const useProcessedTableData = (specificFormatTableData) => {
  const [formulasMapping, setFormulasMapping] = useState({});
  const { getMathFormulas, isLoading, fetchError } = useMathFormulas();

  const processDataWithFormulas = useCallback((recruitsFromBaseTable) => {
    if (recruitsFromBaseTable && recruitsFromBaseTable.length > 0) {
      recruitsFromBaseTable.forEach(recruit => {
        if (recruit && recruit.id && !(recruit.id in formulasMapping)) {
          getMathFormulas(recruit.id)
            .then(data => {
              setFormulasMapping(prev => ({ ...prev, [recruit.id]: data || {} }));
            })
            .catch(error => {
              // Error is logged by useMathFormulas, here we ensure a mapping exists
              console.error(`Error promise caught in useProcessedTableData for recruit ${recruit.id}:`, error.message);
              setFormulasMapping(prev => ({ ...prev, [recruit.id]: {} })); // Prevent re-fetch attempts
            });
        }
      });
    }
    // Call the specific formatter provided by the component
    return specificFormatTableData(recruitsFromBaseTable || [], formulasMapping);
  }, [getMathFormulas, formulasMapping, specificFormatTableData]);

  return {
    processDataWithFormulas,
    isLoadingFormulas: isLoading,
    formulasError: fetchError,
  };
};

export default useProcessedTableData;